import { isEmpty, isNotNil } from 'rambda';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchInput from '@/components/common/search-input';
import UserCarousel, { type UserCarouselItem } from '@/components/common/user-carousel/user-carousel';
import useSearchFriends from '@/hooks/search/use-search-friends';
import useUserInfo from '@/hooks/users/use-user-info';
import { useAuthStore } from '@/stores/auth';
import { ContactList } from './contact-list';

interface Props {
  selectedFriendIds?: number[];
  onSelectFriends?: (friendIds: number[]) => void;
  singleSelect?: boolean;
  disabledUserIds?: number[];
  isContactListHidden?: boolean;
  disableRemove?: boolean;
  maxSelectCount?: number;
}

export interface ContactListRef {
  removeSelectedFriend(id: number): void;
}

function SelectContactList({
  onSelectFriends,
  selectedFriendIds = [],
  singleSelect,
  disabledUserIds,
  isContactListHidden,
  disableRemove,
  maxSelectCount,
}: Props) {
  const { data: userInfo } = useUserInfo();
  const selfId = useAuthStore((s) => s.auth?.UserID);
  const [searchTerm, setSearchTerm] = useState('');
  const { friends, filteredFriends } = useSearchFriends(searchTerm);

  const { t } = useTranslation();

  const listRef = useRef<ContactListRef>(null);

  const selectedFriends = useMemo(
    () =>
      selectedFriendIds
        .map((userId) => {
          if (userId === selfId)
            return {
              userId: selfId,
              avatarUrl: userInfo?.HeadImg,
              label: userInfo?.NickName || '',
            };
          const member = friends.find((f) => f.FriendID === userId);

          return member
            ? ({
                userId: member.FriendID,
                avatarUrl: member.FriendHeadImg,
                label: member.RemarkName || member.FriendNickName,
              } satisfies UserCarouselItem)
            : undefined;
        })
        .filter(isNotNil),
    [selectedFriendIds, friends],
  );
  return (
    <>
      {!isEmpty(selectedFriendIds) && (
        <div className='ml-5'>
          <span className='text-body2'>
            {isContactListHidden ? t('conversation.setting.group.member') : t('contact.create-group.selected')} (
            {selectedFriendIds.length})
          </span>
          <UserCarousel
            users={selectedFriends}
            onRemove={disableRemove ? undefined : listRef.current?.removeSelectedFriend}
          />
        </div>
      )}
      {!isContactListHidden && (
        <SearchInput
          keyword={searchTerm}
          setKeyword={setSearchTerm}
          className='mx-5 mb-4 mt-0 desktop:mx-0'
          clearKeyword={searchTerm ? () => setSearchTerm?.('') : undefined}
        />
      )}
      <ContactList
        ref={listRef}
        friends={filteredFriends}
        onSelectFriends={onSelectFriends}
        isContactListHidden={isContactListHidden}
        singleSelect={singleSelect}
        disabledUserIds={disabledUserIds}
        maxSelectCount={maxSelectCount}
      />
    </>
  );
}

export default SelectContactList;
